import { adminStatsService } from './adminStatsService';

const MAX_VALUE = (arr) => {
  const values = arr.map(x => parseFloat(x) || 0).filter(x => x > 0);
  return Math.max(...values, 1);
};

const normalizeScore = (value, maxValue) => {
  if (!value || maxValue === 0) return 0;
  const normalized = parseFloat(value) / maxValue;
  return Math.min(Math.max(normalized, 0), 1);
};

const calculatePassingAwardScore = (player, allPassingStats) => {
  const pos = (player.POS || '').toUpperCase();
  
  if (pos !== 'QB') return null;
  
  const yrds = parseFloat(player.YRDS) || 0;
  const td = parseFloat(player.TD) || 0;
  const cmpPct = parseFloat(player['CMP%']) || 0;
  const avg = parseFloat(player.AVG) || 0;
  const qbr = parseFloat(player.QBR) || 0;
  const int = parseFloat(player.INT) || 0;
  const pick6 = parseFloat(player.PICK6) || 0;
  const fum = parseFloat(player.FUM) || 0;
  
  const maxYrds = MAX_VALUE(allPassingStats.map(p => p.YRDS));
  const maxTd = MAX_VALUE(allPassingStats.map(p => p.TD));
  const maxCmp = MAX_VALUE(allPassingStats.map(p => p['CMP%']));
  const maxAvg = MAX_VALUE(allPassingStats.map(p => p.AVG));
  const maxQbr = MAX_VALUE(allPassingStats.map(p => p.QBR));
  const maxInt = MAX_VALUE(allPassingStats.map(p => p.INT));
  const maxPick6 = MAX_VALUE(allPassingStats.map(p => p.PICK6));
  const maxFum = MAX_VALUE(allPassingStats.map(p => p.FUM));
  
  const score =
    normalizeScore(yrds, maxYrds) * 0.30 +
    normalizeScore(td, maxTd) * 0.25 +
    normalizeScore(cmpPct, maxCmp) * 0.15 +
    normalizeScore(avg, maxAvg) * 0.10 +
    normalizeScore(qbr, maxQbr) * 0.10 +
    (1 - normalizeScore(int, maxInt)) * 0.05 +
    (1 - normalizeScore(pick6, maxPick6)) * 0.03 +
    (1 - normalizeScore(fum, maxFum)) * 0.02;
  
  return parseFloat(score.toFixed(4));
};

const calculateRushingAwardScore = (player, allRushingStats) => {
  const att = parseFloat(player.ATT) || 0;
  const yrds = parseFloat(player.YRDS) || 0;
  const tds = parseFloat(player.TDS) || 0;
  const avg = parseFloat(player.AVG) || 0;
  const long = parseFloat(player.LONG) || 0;
  const fum = parseFloat(player.FUM) || 0;
  
  const maxYrds = MAX_VALUE(allRushingStats.map(p => p.YRDS));
  const maxTds = MAX_VALUE(allRushingStats.map(p => p.TDS));
  const maxAvg = MAX_VALUE(allRushingStats.map(p => p.AVG));
  const maxLong = MAX_VALUE(allRushingStats.map(p => p.LONG));
  const maxAtt = MAX_VALUE(allRushingStats.map(p => p.ATT));
  const maxFum = MAX_VALUE(allRushingStats.map(p => p.FUM));
  
  const score =
    normalizeScore(yrds, maxYrds) * 0.35 +
    normalizeScore(tds, maxTds) * 0.30 +
    normalizeScore(avg, maxAvg) * 0.10 +
    normalizeScore(long, maxLong) * 0.10 +
    normalizeScore(att, maxAtt) * 0.10 +
    (1 - normalizeScore(fum, maxFum)) * 0.05;
  
  return parseFloat(score.toFixed(4));
};

const calculateReceivingAwardScore = (player, allReceivingStats) => {
  
  const yrds = parseFloat(player.YRDS) || 0;
  const tds = parseFloat(player.TDS) || 0;
  const rec = parseFloat(player.REC) || 0;
  const recPct = parseFloat(player['REC%']) || 0;
  const avg = parseFloat(player.AVG) || 0;
  const long = parseFloat(player.LONG) || 0;
  const fum = parseFloat(player.FUM) || 0;
  
  const maxYrds = MAX_VALUE(allReceivingStats.map(p => p.YRDS));
  const maxTds = MAX_VALUE(allReceivingStats.map(p => p.TDS));
  const maxRec = MAX_VALUE(allReceivingStats.map(p => p.REC));
  const maxRecPct = MAX_VALUE(allReceivingStats.map(p => p['REC%']));
  const maxAvg = MAX_VALUE(allReceivingStats.map(p => p.AVG));
  const maxLong = MAX_VALUE(allReceivingStats.map(p => p.LONG));
  const maxFum = MAX_VALUE(allReceivingStats.map(p => p.FUM));
  
  const score =
    normalizeScore(yrds, maxYrds) * 0.30 +
    normalizeScore(tds, maxTds) * 0.25 +
    normalizeScore(rec, maxRec) * 0.15 +
    normalizeScore(recPct, maxRecPct) * 0.10 +
    normalizeScore(avg, maxAvg) * 0.10 +
    normalizeScore(long, maxLong) * 0.08 +
    (1 - normalizeScore(fum, maxFum)) * 0.02;
  
  return parseFloat(score.toFixed(4));
};

const calculateBlockingAwardScore = (team, allBlockingStats) => {
  const snps = parseFloat(team.SNPS) || 0;
  const sas = parseFloat(team.SAS) || 0;
  
  const maxSas = MAX_VALUE(allBlockingStats.map(t => t.SAS));
  const maxSnps = MAX_VALUE(allBlockingStats.map(t => t.SNPS));
  
  const score =
    (1 - normalizeScore(sas, maxSas)) * 0.80 +
    normalizeScore(snps, maxSnps) * 0.20;
  
  return parseFloat(score.toFixed(4));
};

const calculateDefenseAwardScore = (player, allDefenseStats) => {
  const tck = parseFloat(player.TCK) || 0;
  const sck = parseFloat(player.SCK) || 0;
  const int = parseFloat(player.INT) || 0;
  const td = parseFloat(player.TD) || 0;
  
  const tfl = parseFloat(player.TFL) || 0;
  const sfty = parseFloat(player.SFTY) || 0;
  const ff = parseFloat(player.FF) || 0;
  const fr = parseFloat(player.FR) || 0;
  const frtd = parseFloat(player.FRTD) || 0;
  const pbu = parseFloat(player.PBU) || 0;
  
  const maxTck = MAX_VALUE(allDefenseStats.map(p => p.TCK));
  const maxTfl = MAX_VALUE(allDefenseStats.map(p => p.TFL));
  const maxSck = MAX_VALUE(allDefenseStats.map(p => p.SCK));
  const maxSfty = MAX_VALUE(allDefenseStats.map(p => p.SFTY));
  const maxFf = MAX_VALUE(allDefenseStats.map(p => p.FF));
  const maxFr = MAX_VALUE(allDefenseStats.map(p => p.FR));
  const maxFrtd = MAX_VALUE(allDefenseStats.map(p => p.FRTD));
  const maxInt = MAX_VALUE(allDefenseStats.map(p => p.INT));
  const maxPbu = MAX_VALUE(allDefenseStats.map(p => p.PBU));
  const maxTd = MAX_VALUE(allDefenseStats.map(p => p.TD));
  
  const score =
    normalizeScore(tck, maxTck) * 0.20 +
    normalizeScore(tfl, maxTfl) * 0.10 +
    normalizeScore(sck, maxSck) * 0.20 +
    normalizeScore(sfty, maxSfty) * 0.05 +
    normalizeScore(ff, maxFf) * 0.08 +
    normalizeScore(fr, maxFr) * 0.07 +
    normalizeScore(int, maxInt) * 0.20 +
    normalizeScore(pbu, maxPbu) * 0.05 +
    normalizeScore(frtd, maxFrtd) * 0.02 +
    normalizeScore(td, maxTd) * 0.03;
  
  return parseFloat(score.toFixed(4));
};

const calculateKickingAwardScore = (player, allKickingStats) => {
  
  const fgPct = parseFloat(player['FG%']) || 0;
  const xpPct = parseFloat(player['XP%']) || 0;
  const pnts = parseFloat(player.PNTS) || 0;
  const fgm = parseFloat(player.FGM) || 0;
  const kPct = parseFloat(player['K%']) || 0;
  const long = parseFloat(player.LONG) || 0;
  
  const maxFgPct = MAX_VALUE(allKickingStats.map(p => p['FG%']));
  const maxXpPct = MAX_VALUE(allKickingStats.map(p => p['XP%']));
  const maxPnts = MAX_VALUE(allKickingStats.map(p => p.PNTS));
  const maxFgm = MAX_VALUE(allKickingStats.map(p => p.FGM));
  const maxKPct = MAX_VALUE(allKickingStats.map(p => p['K%']));
  const maxLong = MAX_VALUE(allKickingStats.map(p => p.LONG));
  
  const score =
    normalizeScore(fgPct, maxFgPct) * 0.30 +
    normalizeScore(xpPct, maxXpPct) * 0.15 +
    normalizeScore(pnts, maxPnts) * 0.25 +
    normalizeScore(fgm, maxFgm) * 0.10 +
    normalizeScore(kPct, maxKPct) * 0.10 +
    normalizeScore(long, maxLong) * 0.10;
  
  return parseFloat(score.toFixed(4));
};

export const awardService = {
  async getAwardCandidates(seasonId) {
    try {
      const stats = await adminStatsService.getAllSeasonStats(null, seasonId);
      
      const candidates = {};
      
      if (stats.PASSING && stats.PASSING.length > 0) {
        const qbs = stats.PASSING
          .map(p => ({
            ...p,
            awardScore: calculatePassingAwardScore(p, stats.PASSING)
          }))
          .filter(p => p.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.QBOTY = qbs;
      }
      
      if (stats.RUSHING && stats.RUSHING.length > 0) {
        const rbs = stats.RUSHING
          .filter(p => {
            const pos = (p.POS || '').toUpperCase().trim();
            return pos === 'RB' || pos === 'HB' || pos === 'FB';
          })
          .map(p => ({
            ...p,
            awardScore: calculateRushingAwardScore(p, stats.RUSHING)
          }))
          .filter(p => p.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.RBOTY = rbs;
      }
      
      if (stats.RECEIVING && stats.RECEIVING.length > 0) {
        const wrs = stats.RECEIVING
          .filter(p => (p.POS || '').toUpperCase() === 'WR')
          .map(p => ({
            ...p,
            awardScore: calculateReceivingAwardScore(p, stats.RECEIVING)
          }))
          .filter(p => p.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const tes = stats.RECEIVING
          .filter(p => (p.POS || '').toUpperCase() === 'TE')
          .map(p => ({
            ...p,
            awardScore: calculateReceivingAwardScore(p, stats.RECEIVING)
          }))
          .filter(p => p.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.WROTY = wrs;
        candidates.TEOTY = tes;
      }
      
      if (stats.BLOCKING && stats.BLOCKING.length > 0) {
        const ols = stats.BLOCKING
          .map(t => ({
            ...t,
            awardScore: calculateBlockingAwardScore(t, stats.BLOCKING)
          }))
          .filter(t => t.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.OLOTY = ols;
      }
      
      if (stats.DEFENSE && stats.DEFENSE.length > 0) {
        const allDef = stats.DEFENSE
          .map(p => ({
            ...p,
            awardScore: calculateDefenseAwardScore(p, stats.DEFENSE)
          }))
          .filter(p => p.awardScore !== null);
        
        const scbs = allDef
          .filter(p => {
            const pos = (p.POS || '').toUpperCase();
            return pos === 'SCB' || pos === 'NB';
          })
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const dcbs = allDef
          .filter(p => {
            const pos = (p.POS || '').toUpperCase();
            return pos === 'CB' || pos === 'DCB';
          })
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const lbs = allDef
          .filter(p => (p.POS || '').toUpperCase() === 'LB')
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const des = allDef
          .filter(p => (p.POS || '').toUpperCase() === 'DE')
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const dts = allDef
          .filter(p => (p.POS || '').toUpperCase() === 'DT')
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        const dpoy = allDef
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.SCBOTY = scbs;
        candidates.DCBOTY = dcbs;
        candidates.LBOTY = lbs;
        candidates.DEOTY = des;
        candidates.DTOTY = dts;
        candidates.DPOY = dpoy;
      }
      
      if (stats.KICKING && stats.KICKING.length > 0) {
        const ks = stats.KICKING
          .map(p => ({
            ...p,
            awardScore: calculateKickingAwardScore(p, stats.KICKING)
          }))
          .filter(p => p.awardScore !== null)
          .sort((a, b) => b.awardScore - a.awardScore)
          .slice(0, 5);
        
        candidates.KOTY = ks;
      }
      
      candidates.MVP = this.calculateMVP(stats);
      
      return candidates;
    } catch (error) {
      console.error('Error fetching award candidates:', error);
      return {};
    }
  },

  calculateMVP(stats) {
    const mvpPool = new Map();
    
    if (stats.PASSING) {
      stats.PASSING.forEach(p => {
        const key = (p.NAME || '').toLowerCase();
        if (!mvpPool.has(key)) {
          mvpPool.set(key, {
            NAME: p.NAME,
            TEAM: p.TEAM,
            POS: p.POS,
            passScore: 0,
            rushScore: 0,
            recScore: 0,
            defScore: 0,
            blockScore: 0,
            kickScore: 0
          });
        }
        const score = calculatePassingAwardScore(p, stats.PASSING) || 0;
        const entry = mvpPool.get(key);
        entry.passScore = score;
        if ((p.POS || '').toUpperCase() === 'QB') {
          entry.passScore = score * 1.15;
        }
      });
    }
    
    if (stats.RUSHING) {
      stats.RUSHING.forEach(p => {
        const key = (p.NAME || '').toLowerCase();
        if (!mvpPool.has(key)) {
          mvpPool.set(key, {
            NAME: p.NAME,
            TEAM: p.TEAM,
            POS: p.POS,
            passScore: 0,
            rushScore: 0,
            recScore: 0,
            defScore: 0,
            blockScore: 0,
            kickScore: 0
          });
        }
        const entry = mvpPool.get(key);
        entry.rushScore = calculateRushingAwardScore(p, stats.RUSHING) || 0;
      });
    }
    
    if (stats.RECEIVING) {
      stats.RECEIVING.forEach(p => {
        const key = (p.NAME || '').toLowerCase();
        if (!mvpPool.has(key)) {
          mvpPool.set(key, {
            NAME: p.NAME,
            TEAM: p.TEAM,
            POS: p.POS,
            passScore: 0,
            rushScore: 0,
            recScore: 0,
            defScore: 0,
            blockScore: 0,
            kickScore: 0
          });
        }
        const entry = mvpPool.get(key);
        entry.recScore = calculateReceivingAwardScore(p, stats.RECEIVING) || 0;
      });
    }
    
    if (stats.DEFENSE) {
      stats.DEFENSE.forEach(p => {
        const key = (p.NAME || '').toLowerCase();
        if (!mvpPool.has(key)) {
          mvpPool.set(key, {
            NAME: p.NAME,
            TEAM: p.TEAM,
            POS: p.POS,
            passScore: 0,
            rushScore: 0,
            recScore: 0,
            defScore: 0,
            blockScore: 0,
            kickScore: 0
          });
        }
        const entry = mvpPool.get(key);
        entry.defScore = calculateDefenseAwardScore(p, stats.DEFENSE) || 0;
      });
    }
    
    if (stats.KICKING) {
      stats.KICKING.forEach(p => {
        const key = (p.NAME || '').toLowerCase();
        if (!mvpPool.has(key)) {
          mvpPool.set(key, {
            NAME: p.NAME,
            TEAM: p.TEAM,
            POS: p.POS,
            passScore: 0,
            rushScore: 0,
            recScore: 0,
            defScore: 0,
            blockScore: 0,
            kickScore: 0
          });
        }
        const entry = mvpPool.get(key);
        entry.kickScore = calculateKickingAwardScore(p, stats.KICKING) || 0;
      });
    }
    
    return Array.from(mvpPool.values())
      .map(p => ({
        ...p,
        awardScore: parseFloat((
          (p.passScore || 0) +
          (p.rushScore || 0) +
          (p.recScore || 0) +
          (p.defScore || 0) +
          (p.blockScore || 0) +
          (p.kickScore || 0)
        ).toFixed(4))
      }))
      .filter(p => p.awardScore > 0)
      .sort((a, b) => b.awardScore - a.awardScore)
      .slice(0, 5);
  }
};
